import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { AppState, AppTheme, ProfileReducerState, VerseInterface, VerseReducerState, VersesReducerState } from "../../../interfaces";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import {
  Button,
  Grid,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  InputAdornment
} from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { DialogModal, ErrorView } from "../../../components";
import { clearVerseError, getVerses, deleteVerse } from "../../../store/actions";
import { checkUserAccess } from "../../../util/permission-checker";
import PopupMenuDialog from "../../../components/PopupMenuDialog";
import UpsertVerseDetailsModal from "./Modals/UpsertVerseDetailsDialog";
import dateformat from "dateformat";
import ViewFullVerseDialog from "./Modals/ViewFullVerseDialog";

interface VersesProps {
  verses: VersesReducerState;
  verse: VerseReducerState;
  profile: ProfileReducerState;
  appTheme: AppTheme;
  getVerses: (search: string, page: number, per_page: number) => void;
  clearVerseError: () => void;
  deleteVerse: (_id: string) => void;
}

const useStyles = (appTheme: AppTheme) =>
  makeStyles((theme: Theme) =>
    createStyles({
      root: {
        flexGrow: 1,
        width: "100%"
      },
      paper: {
        backgroundColor: appTheme.section.backgroundColor,
        width: "100%",
        padding: 30,
        marginTop: 20,
        marginBottom: 20
      },
      icon: {
        color: theme.palette.text.primary,
        padding: 10,
        borderRadius: 30,
        backgroundColor: appTheme.iconBackgroundColor,
        "&:hover": {
          backgroundColor: appTheme.iconBackgroundHoverColor
        }
      },
      searchButton: {
        marginRight: "-21px",
        height: "55px",
        borderRadius: "30px",
        width: "100px"
      },
      textInput: {
        marginTop: "5px",
        width: "300px"
      },
      textInputControl: {
        borderRadius: "30px",
        backgroundColor: appTheme.section.backgroundColor
      },
      container: {
        minHeight: "60vh"
      },
      textInputIcon: {
        color: "#000"
      }
    })
  );

const Verses: React.FC<VersesProps> = (props) => {
  const { appTheme, verses, verse, profile, getVerses, deleteVerse, clearVerseError } = props;

  const [search, setSearch] = useState<string>(verses.search);
  const [page, setPage] = useState<number>(verses.page);
  const [perPage, setPerPage] = useState<number>(verses.per_page ?? 20);
  const [selectedRecord, setSelectedRecord] = useState<VerseInterface | null>(null);
  const [popUpDialogOpen, setPopUpDialogOpen] = useState<boolean>(false);

  const [upsertVerseDetailsModelOpen, setUpsertVerseDetailsModelOpen] = useState<boolean>(false);
  const [viewFullVerseModelOpen, setViewFullVerseModelOpen] = useState<boolean>(false);

  const popUpMenuItems = useMemo(() => {
    return [
      { text: "Update Verse Details", permission: "edit-verse" },
      { text: "View Full Verse", permission: "view-verses" },
      { text: "Delete Verse", permission: "delete-verse" }
    ];
  }, []);

  const [menuItems, setMenuItems] = useState<Array<string>>(
    popUpMenuItems.filter((menuItem) => checkUserAccess(menuItem.permission, profile.permissions)).map((menuItem) => menuItem.text)
  );

  useEffect(() => {
    setMenuItems(popUpMenuItems.filter((menuItem) => checkUserAccess(menuItem.permission, profile.permissions)).map((menuItem) => menuItem.text));
  }, [popUpMenuItems, profile.permissions]);

  useEffect(() => {
    getVerses(search, page, perPage);
  }, [search, page, perPage, getVerses]);

  const styles = useStyles(appTheme)();

  type DialogOptions = {
    open: boolean;
    body: string;
  };

  const [dialogOptions, setDialogOptions] = useState<DialogOptions>({ open: false, body: "" });

  const dismissDialogModal = useCallback(() => {
    setDialogOptions({ open: false, body: "" });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => {
      clearVerseError();
    };
  }, [clearVerseError]);

  useEffect(() => {
    if (!verse.error && verse.response) {
      if (verse.response.data.status === "Ok") {
        clearVerseError();
        return;
      }

      clearVerseError();

      setSelectedRecord(null);

      setUpsertVerseDetailsModelOpen(false);

      setViewFullVerseModelOpen(false);

      setDialogOptions({ open: true, body: verse.response.data.status });

      getVerses("", 0, perPage);
    }
  }, [verse.error, verse.response, perPage, getVerses, clearVerseError]);

  useEffect(() => {
    if (upsertVerseDetailsModelOpen || viewFullVerseModelOpen) {
      return;
    }

    const requestData = verse.error ? verse : null;

    if (!requestData) {
      return;
    }

    if (requestData.error && requestData.error.data && requestData.error.status) {
      let body: string = requestData.error.data.status;

      if (requestData.error.status === 500) {
        body = requestData.error.data.content;
      }

      setDialogOptions({ open: true, body: body });
    } else {
      if (requestData.error && requestData.error.message) {
        setDialogOptions({ open: true, body: requestData.error.message });
      }
    }
  }, [verse, upsertVerseDetailsModelOpen, viewFullVerseModelOpen]);

  const onMenuItemSelected = (menuItem: string) => {
    setPopUpDialogOpen(false);

    if (!selectedRecord) {
      return;
    }

    switch (menuItem) {
      case "Update Verse Details":
        setUpsertVerseDetailsModelOpen(true);
        break;
      case "View Full Verse":
        setViewFullVerseModelOpen(true);
        break;
      case "Delete Verse":
        if (window.confirm("Would you like to delete this verse ?")) {
          deleteVerse(selectedRecord.verse_id);
        }
        break;
      default:
        break;
    }
  };
  return (
    <div className={styles.root}>
      <DialogModal open={dialogOptions.open} body={dialogOptions.body} onDismissed={dismissDialogModal} />
      <UpsertVerseDetailsModal
        open={upsertVerseDetailsModelOpen}
        onDismissed={() => setUpsertVerseDetailsModelOpen(false)}
        verseData={selectedRecord}
      />

      <ViewFullVerseDialog open={viewFullVerseModelOpen} onDismissed={() => setViewFullVerseModelOpen(false)} verseData={selectedRecord} />
      <PopupMenuDialog
        open={popUpDialogOpen}
        onDismissed={() => setPopUpDialogOpen(false)}
        onMenuItemSelected={onMenuItemSelected}
        menuItems={menuItems}
      />
      <Container maxWidth="lg">
        {verses.error ? (
          <ErrorView error={verses.error} onRetry={() => getVerses(search, page, perPage)} />
        ) : (
          <React.Fragment>
            <Paper className={styles.paper}>
              <Grid container justifyContent="space-between" spacing={0}>
                <Grid item>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      {(verses.loading || verse.loading) && <CircularProgress style={{ display: "block", margin: "auto" }} color="primary" />}
                    </Grid>
                    <Grid item>
                      <Typography variant="h3" color="primary">
                        Verses
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <TextField
                    onChange={(verse) => {
                      setSearch(verse.currentTarget.value);
                    }}
                    error={false}
                    value={search}
                    variant="outlined"
                    placeholder="Search ..."
                    className={styles.textInput}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search className={styles.textInputIcon} />
                        </InputAdornment>
                      ),
                      classes: { root: styles.textInputControl }
                    }}
                  />
                </Grid>
              </Grid>
              <br />
              <TableContainer className={styles.container}>
                <Table stickyHeader aria-label="verses">
                  <TableHead>
                    <TableRow>
                      {[
                        { label: "ID", minWidth: 100 },
                        { label: "Date", minWidth: 200 },
                        { label: "Verse", minWidth: 300 },
                        { label: "Options", minWidth: 100 }
                      ].map((column) => (
                        <TableCell key={column.label} align="center" style={{ minWidth: column.minWidth }}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {verses.data.map((verse: VerseInterface) => {
                      const _id = verse.verse_id;
                      return (
                        <TableRow hover role="checkbox" tabIndex={-1} key={_id}>
                          {["verse_id", "verse_date", "verse", "options"].map((column) => {
                            const key = `${_id}-${column}`;

                            const data = (verse as unknown as { [key: string]: any })[column];
                            if (column === "options") {
                              if (menuItems.length === 0) return <TableCell key={key} align="center"></TableCell>;
                              return (
                                <TableCell key={key} align="center">
                                  <Button
                                    variant="outlined"
                                    disabled={menuItems.length === 0}
                                    onClick={() => {
                                      setSelectedRecord(verse);
                                      setPopUpDialogOpen(true);
                                    }}>
                                    Options
                                  </Button>
                                </TableCell>
                              );
                            } else if (column === "verse_date") {
                              return (
                                <TableCell key={key} align="center">
                                  {dateformat(data, "ddd mmm dd yyyy")}
                                </TableCell>
                              );
                            } else {
                              return (
                                <TableCell key={key} align="center">
                                  {data}
                                </TableCell>
                              );
                            }
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid container justifyContent="space-between" style={{ margin: "0px 10px 0px 10px" }}>
                <Grid item>
                  <Button
                    disabled={!checkUserAccess("add-verse", profile.permissions)}
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      setSelectedRecord(null);
                      setUpsertVerseDetailsModelOpen(true);
                    }}>
                    Add New Verse
                  </Button>
                </Grid>
                <Grid item>
                  <TablePagination
                    component="div"
                    count={verses.total}
                    page={page}
                    onPageChange={(_, page) => setPage(page)}
                    rowsPerPage={perPage}
                    rowsPerPageOptions={[20, 50, 100, 1000]}
                    onRowsPerPageChange={(verse) => {
                      setPerPage(parseInt(verse.target.value));
                      setPage(0);
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </React.Fragment>
        )}
      </Container>
    </div>
  );
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, void, AnyAction>) => {
  return {
    getVerses: (search: string, page: number, per_page: number) => dispatch(getVerses(search, page, per_page)),
    clearVerseError: () => dispatch(clearVerseError()),
    deleteVerse: (_id: string) => dispatch(deleteVerse(_id))
  };
};

const mapStateToProps = (state: AppState) => {
  return {
    verses: state.verses,
    verse: state.verse,
    profile: state.profile,
    appTheme: state.general.theme
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Verses);
